import { ChevronLeft } from 'lucide-preact'
import { useLayoutEffect, useRef } from 'preact/hooks'
import type { ChatView, MessageView } from '#contract'
import { text } from '#contract'
import { dayText } from '../../../i18n/dates.ts'
import { useLocale, useT } from '../../../i18n/index.ts'
import { formatOsTime, gameDay, timeOfDay } from '../../clock.ts'
import { Avatar } from './avatar.tsx'
import styles from './wassup.module.css'

interface ThreadProps {
  readonly chat: ChatView
  /** The phone's back to the list; the desktop shows both, so it has none. */
  readonly onBack: (() => void) | null
}

/** One chat: its bar, then its messages as bubbles under a chip for each day, the newest last. */
export function Thread({ chat, onBack }: ThreadProps) {
  const t = useT()
  const scrollRef = useRef<HTMLDivElement>(null)
  const count = chat.messages.length
  // Opened, or a message arrived: the newest is in view, as in any messenger.
  useLayoutEffect(() => {
    const element = scrollRef.current
    if (element) element.scrollTop = element.scrollHeight
  }, [chat.id, count])

  return (
    <div class={styles.thread}>
      <header class={styles.threadBar}>
        {onBack && (
          <button
            type="button"
            class={styles.back}
            aria-label={t(text('os.app.wassup.back'))}
            onClick={onBack}
          >
            <ChevronLeft aria-hidden="true" />
          </button>
        )}
        <Avatar chat={chat} small />
        <span class={styles.threadTitle}>
          <span class={styles.threadName}>{t(chat.name)}</span>
          {chat.app !== null && (
            <span class={styles.threadSub}>{t(text('os.app.wassup.official'))}</span>
          )}
        </span>
      </header>
      <div ref={scrollRef} class={styles.messages}>
        {groupByDay(chat.messages).map(([day, messages]) => (
          <DayGroup key={day} day={day} messages={messages} />
        ))}
      </div>
    </div>
  )
}

function DayGroup({ day, messages }: { readonly day: number; readonly messages: MessageView[] }) {
  const t = useT()
  const locale = useLocale()
  return (
    <>
      <p class={styles.chip}>{t(dayText(locale, day))}</p>
      {messages.map((message) => (
        <p key={message.id} class={styles.bubble}>
          {t(message.text)}
          <time class={styles.stamp}>{formatOsTime(timeOfDay(message.at))}</time>
        </p>
      ))}
    </>
  )
}

/** Messages in runs of the same game day, in order. */
function groupByDay(messages: readonly MessageView[]): [number, MessageView[]][] {
  const groups: [number, MessageView[]][] = []
  for (const message of messages) {
    const day = gameDay(message.at)
    const group = groups.at(-1)
    if (group?.[0] === day) group[1].push(message)
    else groups.push([day, [message]])
  }
  return groups
}
